import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../../../components/base/Modal";
import { closeJob, createJob, getCountryByCode, getDefaultActiveCountry, getUSA, getUser, isActiveCountry, hasAccessToJobType, hasAccessToMetro, hasAccessToSource, useFlags, useGlobal, useHub, useJobMetros, useJobSources, useJobTypes, usePreferences, useScreen, useStatusesForCreateJob, useStatusesForJob, useTags, useEnsureJobColors } from "../../../../core/Global";
import TechColorDot from "../components/TechColorDot";
import JobFormMap from "../../../../components/map/JobFormMap";
import { useTechRanking } from "../../../../components/map/useTechRanking";
import { metroCovers } from "../../../../components/map/mapUtils";
import { renderMetroOption, renderMetroSelection, useMetroOptions } from "../../base/components/MetroOption";
import TextBox from "../../../../components/form/TextBox";
import DropDown from "../../../../components/form/DropDown";
import Select from "../../../../components/form/Select";
import Avatar from "../../../../components/base/Avatar";
import TechAvailabilityLabel from "../../../../components/tech/TechAvailabilityLabel";
import TechPriorityBadges from "../../../../components/tech/TechPriorityBadges";
import { API_ROUTES } from "../../../../core/Constants";
import Tagify from "../../../../components/tagify/Tagify";
import DatePicker from "../../../../components/form/DatePicker";
import TextArea from "../../../../components/form/TextArea";
import AddressField from "../../../../components/here/AddressField";
import { HereGeocodeItem } from "../../../../models/MapModels";
import PhoneInput from "../../../../components/form/PhoneInput";
import Icon from "../../../../components/base/Icon";
import JobRequestModel from "../../../../models/JobRequestModel";
import TimePicker from "../../../../components/form/TimePicker";
import FlexRow from "../../../../components/base/FlexRow";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import Job, { JobApptReminderItem } from "../models/Job";
import Technician from "../../../technicians/models/technician";
import TechAvailabilityIndicator from "../components/TechAvailabilityIndicator";
import Indicator from "../../../../components/base/Indicator";
import JobMetro from "../../base/models/JobMetro";
import JobStatus from "../../base/models/JobStatus";
import Country from "../../../base/models/Country";
import DateItem from "../../../../models/DateItem";
import PhoneItem from "../../../../models/PhoneItem";
import Contact from "../../../contacts/models/Contact";
import IconButton from "../../../../components/base/IconButton";
import moment from "moment";



interface IProps {
    duplicateId?: string
    chatMessageId?: string
    liveCallRefId?: string
    prefillPhone?: string
    prefillJobSourceId?: string
}

// Rich technician option for the Technician <Select>: avatar, name, current
// availability (+ today's hours), and distance to the job. Availability comes
// pre-computed from the backend (tech.availability); distance (tech.distanceLabel)
// is only present when a live location or wakeup address could be resolved.
function renderTechOption(tech: any) {
    return (
        <FlexRow className="w-100">
            <Avatar avatarUrl={tech.avatar} fullName={tech.fullName} width={28} />
            <div className="d-flex flex-column ms-3 flex-grow-1 min-w-0">
                <span className="d-flex align-items-center fw-bold text-gray-800 text-truncate">
                    {tech.fullName}
                    <TechColorDot colorId={tech.techJobColorId} />
                    {
                        // As in the Metro & Technician drawer: an app technician on the clock is
                        // "free now" and listed first, so the list says why it is in this order.
                        tech.hasApp &&
                        <span className={"badge fs-9 ms-2 " + (tech.onClock ? "badge-success" : "badge-light")}>
                            <Icon icon="user-clock" type="solid" size={9} className="me-1" />
                            {tech.onClock ? "On the clock" : "Off the clock"}
                        </span>
                    }
                </span>
                <TechAvailabilityLabel availability={tech.availability} className="fs-8 mt-1" />
                <span className="fs-9 text-gray-500 mt-1">{tech.distanceLabel ?? "Unknown Distance"}</span>
            </div>
            <TechPriorityBadges tech={tech} className="ms-auto ps-2" />
        </FlexRow>
    );
}

function renderTechSelection(tech: any) {
    return (
        <span className="d-flex align-items-center">
            <Avatar avatarUrl={tech.avatar} fullName={tech.fullName} width={22} />
            <span className="ms-2 text-truncate">{tech.fullName}</span>
            <TechColorDot colorId={tech.techJobColorId} />
        </span>
    );
}

export default function JobFormDrawer(props: IProps) {

    const drawerRef = useRef<DrawerRef>(null)

    const [globalState] = useGlobal()

    const pref = usePreferences()
    const hub = useHub()
    // For the technicians' job colour dots in the Technician select.
    useEnsureJobColors()
    const screen = useScreen()
    const user = getUser()

    const jobTypes = useJobTypes().filter(t => hasAccessToJobType(t.id))
    const jobSources = useJobSources().filter(s => hasAccessToSource(s.id))
    //const jobMetros = useJobMetros()
    const flagsBank = useFlags()
    const tagsBank = useTags()


    const setCountryCache = (count: Country) => {
        window.localStorage.setItem("crm-jobs-country-cache", JSON.stringify(count));
    }

    // The last country used on this screen, as long as it is still one of the account's active
    // countries; otherwise the default active one (US when active, else the first).
    const getCountryCache = () => {
        var data = window.localStorage.getItem("crm-jobs-country-cache");
        if (!data || data == "") return getDefaultActiveCountry();
        var cached: Country = JSON.parse(data);
        return isActiveCountry(cached?.id) ? cached : getDefaultActiveCountry();
    }

    const [country, countrySet] = useState<Country>(getCountryCache());
    const [mapCountry, mapCountrySet] = useState<Country>(getCountryCache());
    const [phoneCountry, phoneCountrySet] = useState<Country>(getCountryCache());
    const [states, statesSet] = useState<DateItem[]>();

    useEffect(() => {
        if (phoneCountry) {
            if (!address && mapCountry.id != phoneCountry.id) {
                mapCountrySet(phoneCountry);
            }
            phoneSet(undefined);
        }
    }, [phoneCountry])

    useEffect(() => {
        if (mapCountry) {
            if (!phone && phoneCountry.id != mapCountry.id) {
                phoneCountrySet(mapCountry);
            }
            countrySet(mapCountry);
        }
    }, [mapCountry])

    useEffect(() => {
        if (country) {
            setCountryCache(country);
            addressSet(undefined);
            customAddressSet(undefined);
            hub.Get("/base/states/?countryId=" + country.id, res => {
                if (res.data) statesSet(res.data);
            });
        }
    }, [country])


    const [phone, phoneSet] = useState<PhoneItem>();
    const [contact, contactSet] = useState<Contact>();
    const [fullName, fullNameSet] = useState<string>();
    const [address, addressSet] = useState<HereGeocodeItem>();
    const [selectedState, selectedStateSet] = useState<string>();
    const [zipcode, zipcodeSet] = useState<string>();
    const [customAddress, customAddressSet] = useState<string>();

    useEffect(() => {
        var zipcode = "";
        var validChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-']
        if (customAddress) {
            for (var i = 0; i < customAddress.length; i++) {
                var c = customAddress[i];
                if (validChars.indexOf(c) < 0) zipcode = "";
                else zipcode += c;

                if (zipcode.length > 4) break;
            }
        }

        if (zipcode.length > 4) {
            hub.LookUpZipcode(zipcode, country.alpha3Code ?? "usa", res => {
                zipcodeSet(res?.address?.postalCode)
                selectedStateSet(res?.address?.stateId)
            })
        }
    }, [customAddress])

    const [contactPhone, contactPhoneSet] = useState<PhoneItem>();
    // Remount key for the Secondary Phone field. Its <PhoneInput> applies defaultPhone only once,
    // so bump this to force a fresh mount that picks up the selected contact's secondary number.
    const [contactFillKey, contactFillKeySet] = useState<number>(0);

    useEffect(() => {
        if (contact) {
            phoneSet(contact.phone)
            fullNameSet(contact.fullName)
            addressSet(contact.address)
            // Autofill the secondary phone too — only when the contact actually has one, so we
            // don't wipe a value the user may have already typed.
            if (contact.phoneContact?.phone) {
                contactPhoneSet(contact.phoneContact)
                contactFillKeySet(k => k + 1)
            }
        }
    }, [contact])
    const [company, companySet] = useState<string>();
    const [email, emailSet] = useState<string>();

    const [jobSourceId, jobSourceIdSet] = useState<string>();
    const [jobTypeId, jobTypeIdSet] = useState<string>();


    const [jobMetros, metrosSet] = useState<JobMetro[]>();
    const metroOptions = useMetroOptions(jobMetros, address?.position);
    const [selectedMetro, selectedMetroSet] = useState<JobMetro>();
    const fetchMetros = () => {
        hub.Get(API_ROUTES.JobMetros + "all", res => {
            if (res.success) metrosSet((res.data ?? []).filter((m: JobMetro) => hasAccessToMetro(m.id)));
        })
    }

    useEffect(() => {
        fetchMetros()
    }, [])


    const [techs, techsSet] = useState<Technician[]>();
    const [selectedTech, selectedTechSet] = useState<Technician>();
    // Where each technician is, their drive to the job's address, whether they are free, and the
    // order -- the same as in the Metro & Technician drawer.
    const ranking = useTechRanking(techs, address?.position);
    const fetchTechs = () => {
        ranking.beginLoad();

        var q = "?sourceId=" + jobSourceId;
        q += "&typeId=" + jobTypeId;
        q += "&metroId=" + selectedMetro?.id;

        // Pass the job address so the backend can compute each tech's distance
        // (live location preferred, then wakeup address).
        if (address?.position?.lat && address?.position?.lng) {
            q += "&lat=" + address.position.lat;
            q += "&lng=" + address.position.lng;
        }

        hub.Get("/accounts/Technicians/byatts/" + q, res => {
            if (res.success) techsSet(res.data);
            else ranking.endLoad();
        })
    }


    useEffect(() => {
        fetchTechs()
    }, [jobSourceId, jobTypeId, selectedMetro, address])

    const [jobDetails, jobDetailsSet] = useState<string>();

    const [flags, flagsSet] = useState<string[]>([]);
    const [tags, tagsSet] = useState<string[]>([]);

    const [appointmentDate, appointmentDateSet] = useState<string>();
    const [appointmentDateText, appointmentDateTextSet] = useState<string>();
    const [appointmentTimeFrom, appointmentTimeFromSet] = useState<string>();
    const [appointmentTimeTo, appointmentTimeToSet] = useState<string>();

    const statuses = useStatusesForCreateJob()
    const [selectedStatus, selectedStatusSet] = useState<JobStatus>(statuses[0]);

    // When an appointment date is set, move the job status to the configured
    // "Appointment" status (the one flagged isAppointment). When the appointment is
    // cleared, revert to the default status only if it was auto-selected here, so a
    // manual status choice is never overridden.
    useEffect(() => {
        const apptStatus = statuses?.find(f => f.isAppointment);
        if (!apptStatus) return;
        if (appointmentDate) {
            selectedStatusSet(apptStatus);
        } else {
            selectedStatusSet(prev => prev?.id === apptStatus.id ? statuses[0] : prev);
        }
    }, [appointmentDate]);

    // Statuses load asynchronously and may not be ready when the form is opened from the
    // chat "Add as Job" flow, leaving the status button empty/disabled. Once they arrive,
    // pick the default status (the appointment one if an appointment is set). Never overrides
    // a status that's already selected.
    useEffect(() => {
        if (selectedStatus) return;
        if (!statuses || statuses.length === 0) return;
        const apptStatus = statuses.find(f => f.isAppointment);
        selectedStatusSet(appointmentDate && apptStatus ? apptStatus : statuses[0]);
    }, [statuses, appointmentDate]);

    const [opened, openedSet] = useState<boolean>(false);

    const [duplicateModel, duplicateModelSet] = useState<Job>();
    const [duplicateLoading, duplicateLoadingSet] = useState<boolean>(false);

    const [buildJobMap, buildJobMapSet] = useState<boolean>(false);
    const [mapZoom, mapZoomSet] = useState<number>();


    const [addressOnceSelected, addressOnceSelectedSet] = useState<boolean>(false);


    const [formReset, formResetSet] = useState<boolean>(false);

    const [showMore, showMoreSet] = useState<boolean>(false);
    const [minToTime, minToTimeSet] = useState<number>();

    const [bodyChanged, bodyChangedSet] = useState<boolean>(false);
    useEffect(() => {
        if (!window.isNullOrEmpty(address?.id) || !window.isNullOrEmpty(phone?.phone)
            || !window.isNullOrEmpty(fullName) || !window.isNullOrEmpty(jobSourceId)
            || !window.isNullOrEmpty(jobTypeId) || !window.isNullOrEmpty(jobDetails)) bodyChangedSet(true);
    }, [address, phone, fullName, jobSourceId, jobTypeId, jobDetails])



    const [keepOpen, keepOpenSet] = useState<boolean>(false);

    const [submiting, submitingSet] = useState<boolean>(false);
    const [sending, sendingSet] = useState<boolean>(false);



    useEffect(() => {
        if (props.duplicateId) {
            duplicateLoadingSet(true);

            hub.Get(API_ROUTES.Jobs + props.duplicateId + "?addmetros=True", res => {
                if (res.success) {
                    duplicateModelSet(res.data);
                    duplicateLoadingSet(false);
                }
            });

        }
    }, [props.duplicateId])

    useEffect(() => {
        if (props.liveCallRefId) {
            duplicateLoadingSet(true);

            hub.Get(API_ROUTES.Jobs + "fillbycall/" + props.liveCallRefId, res => {

                console.log("fillbycall result", res.data)
                if (res.success) {
                    duplicateModelSet(res.data);
                    duplicateLoadingSet(false);
                }
            });

        }
    }, [props.liveCallRefId])

    useEffect(() => {
        if (duplicateModel) {

            phoneSet(duplicateModel.phone)
            fullNameSet(duplicateModel.fullName)
            addressSet(duplicateModel.address)
            contactPhoneSet(duplicateModel.phoneContact)
            emailSet(duplicateModel.emailAddress)
            companySet(duplicateModel.company)
            jobSourceIdSet(duplicateModel.jobSourceId)
            jobTypeIdSet(duplicateModel.jobTypeId)
            selectedStateSet(duplicateModel.stateId)

        }
    }, [duplicateModel])


    useEffect(() => {
        if (!props.prefillPhone && !props.prefillJobSourceId) return;

        if (props.prefillPhone) {
            const digits = props.prefillPhone.replace(/[^\d]/g, "").replace(/^1(?=\d{10}$)/, "");
            const c = getCountryByCode("us") ?? getUSA();
            phoneSet({
                countryCode: "+" + (c?.phoneCode ?? 1),
                countryId: c?.id,
                phone: digits
            });
        }

        if (props.prefillJobSourceId) {
            jobSourceIdSet(props.prefillJobSourceId);
        }
    }, [props.prefillPhone, props.prefillJobSourceId])

    useEffect(() => {
        if (props.chatMessageId) {
            buildJobMapSet(false);
            duplicateLoadingSet(true);
            hub.Get(API_ROUTES.Jobs + "msgtojob/" + props.chatMessageId, res => {
                if (res.success) {
                    fullNameSet(res.data.name);
                    var c = getCountryByCode(res.data.country);
                    if (c) {
                        countrySet(c);
                    }
                    // The parser answers "+14013689975"; the form (and every saved job) keeps the
                    // national digits only, or past-jobs lookups never match this customer.
                    const national = (p?: string) => {
                        var d = (p ?? "").replace(/\D/g, "");
                        var code = String(c?.phoneCode ?? 1);
                        if (p?.trim().startsWith("+") && d.startsWith(code)) d = d.substring(code.length);
                        else if (code == "1" && d.length == 11 && d.startsWith("1")) d = d.substring(1);
                        return d;
                    }
                    phoneSet({
                        countryCode: "+" + (c?.phoneCode ?? 1),
                        countryId: c?.id,
                        phone: national(res.data.phone),
                        extension: res.data.phoneExt
                    })
                    if (res.data.phone2) {
                        contactPhoneSet({
                            countryCode: "+" + (c?.phoneCode ?? 1),
                            countryId: c?.id,
                            phone: national(res.data.phone2),
                            extension: res.data.phone2Ext
                        })
                    }
                    jobDetailsSet(res.data.jobDetails);
                    jobTypeIdSet(res.data.jobTypeId);
                    appointmentDateSet(res.data.appointmentDate);
                    appointmentDateTextSet(res.data.appointmentDate);
                    appointmentTimeFromSet(res.data.appointmentTimeFrom);
                    jobSourceIdSet(res.data.jobSourceId);
                    addressSet(res.data.addressItem);
                    if (res.data.addressItem && !res.data.addressItem.id) customAddressSet(res.data.addressItem.title);
                }
                duplicateLoadingSet(false)
                buildJobMapSet(true)
            });
        }
    }, [props.chatMessageId])



    const mapWidth = () => {
        var winWidth = (globalState.windowsWidth ?? 1000);
        if (winWidth > 1600) return winWidth / 2.5;
        else return winWidth / 1;
    }

    const mapHeight = () => {
        var winWidth = (globalState.windowsWidth ?? 1000);
        var winHeight = (globalState.windowsHeight ?? 760);
        if (winWidth > 1600) return winHeight;
        else return winHeight / 3;
    }

    useEffect(() => {

        setTimeout(() => {
            openedSet(true)
        }, 500);

        var k = window.localStorage.getItem("dspvoice-job-form-keepopen");
        if (k && k == "1") keepOpenSet(true);

    }, [])



    useEffect(() => {
        window.localStorage.setItem("dspvoice-job-form-keepopen", keepOpen ? "1" : "0");
    }, [keepOpen])



    useEffect(() => {
        if (buildJobMap) {
            setTimeout(() => {
                buildJobMapSet(false);
            }, 1000);
        }

        if (formReset) {
            setTimeout(() => {
                formResetSet(false);
            }, 1000);
        }

    }, [buildJobMap, formReset])


    useEffect(() => {
        if (address) {
            window.hideFormError("jobAddress");
            selectedStateSet(address.address?.stateId);
            addressOnceSelectedSet(true);
        }
    }, [address])


    // Whichever way the address changes -- typed, the pin dragged, a contact, a chat message, a
    // duplicated job -- the metro follows it: the one the server found for it, else the first metro
    // whose area (circle, rectangle or polygon) covers it, else none, so a metro picked for an
    // earlier address does not stay behind.
    useEffect(() => {
        if (address && jobMetros) {
            const metro = jobMetros.find(mt => mt.id == address.metroId)
                ?? jobMetros.find(mt => metroCovers(mt, address.position));
            selectedMetroSet(metro);
        }
        else if (addressOnceSelected) {
            selectedMetroSet(undefined);
        }
    }, [address, jobMetros])



    const [submitAndSend, submitAndSendSet] = useState<boolean>();
    useEffect(() => {
        if (submitAndSend) {
            submit(true)
        }
    }, [submitAndSend])

    const submit = (send?: boolean, anyway?: boolean) => {

        let valid = true;
        let focused = false;
        window.hideFormErrors();

        if ((phone?.phone?.length ?? 0) < 5) {
            window.showFormError("phoneNumber", "Please write a valid phone number!", false, "drawer-job-form", focused);
            valid = false;
            focused = true;
        }

        if (!fullName || fullName.length < 2) {
            window.showFormError("fullname", "Please write a valid name!", false, "drawer-job-form", focused);
            valid = false;
            focused = true;
        }

        if (window.isNullOrEmpty(jobSourceId) && !user?.isTech) {
            window.showFormError("jobSourceId", "Required!", false, "drawer-job-form", focused);
            valid = false;
            focused = true;
        }

        if (window.isNullOrEmpty(jobTypeId)) {
            window.showFormError("jobTypeId", "Required!", false, "drawer-job-form", focused);
            valid = false;
            focused = true;
        }



        if (send) {

            if (!address && (customAddress?.length ?? 0) < 5) {
                window.showFormError("jobAddress", "Please write a valid address!", false, "drawer-job-form", focused);
                valid = false;
                focused = true;
            }

            if (!selectedMetro) {
                window.showFormError("metroId", "Required!", false, "drawer-job-form", focused);
                valid = false;
                focused = true;
            }

            if (!selectedTech) {
                window.showFormError("techId", "Required!", false, "drawer-job-form", focused);
                valid = false;
                focused = true;
            }
            // if (!body.jobDetails) {
            //     window.showFormError("jobDetails", "Please write the job details!", false, "drawer-job-form");
            //     valid = false;
            // }
        }

        // if (appointmentDate && !appointmentTimeFrom) {
        //     window.showFormError("appointmentFrom", "Please set the appointment time!", true, "drawer-job-form", focused);
        //     valid = false;
        //     focused = true;
        // }

        // if (appointmentDate && (apptTimeRange || pref?.jobsPreferences.forceTimeRangeForAppt) && !appointmentTimeTo) {
        //     window.showFormError("appointmentTo", "Please set the appointment time!", true, "drawer-job-form", focused);
        //     valid = false;
        //     focused = true;
        // }

        reminders.forEach(r => {
            if (r.type == 1 || r.type == 2) {
                if ((r.amount ?? 0) < 1) {
                    window.formError(`reminder-amount-${r.id}`, "", "drawer-job-form")
                    valid = false;
                }
            }
            if (r.type == 3) {
                if (!r.date || moment(r.date).year() < moment().year()) {
                    window.formError(`reminder-date-${r.id}`, "", "drawer-job-form")
                    valid = false;
                }
            }
        });

        if (!valid) {
            submitAndSendSet(false);
            return;
        }

        if (!address?.id && !anyway) {
            window.openModal("modal-job-address-incomplete");
            return;
        }

        if (appointmentDate && appointmentTimeFrom && reminders.length == 0 && !anyway) {
            window.openModal("modal-noapptreminder-confirm");
            return;
        }

        if (send) sendingSet(true);
        else submitingSet(true);

        submitAndSendSet(false);

        let body: JobRequestModel = {
            phone: phone,
            fullName: fullName,
            addressId: address?.id,
            company: company,
            contactId: contact?.id,
            emailAddress: email,
            contactPhone: contactPhone,
            customAddress: customAddress,
            countryId: country.id,
            jobDetails: jobDetails,
            flagIds: flags,
            tagIds: tags,
            jobSourceId: jobSourceId,
            jobTypeId: jobTypeId,
            metroId: selectedMetro?.id,
            techId: selectedTech?.id,
            send: send ?? false,
            appointmentDate: appointmentDateText,
            appointmentTimeFrom: appointmentTimeFrom,
            appointmentTimeTo: appointmentTimeTo,
            jobStatusId: selectedStatus?.id,
            stateId: selectedState,
            zipCode: zipcode,
            reminders: reminders,
            mapZoom: mapZoom
        }

        hub.Post("/crm/jobs/", body, res => {

            if (res.success) {
                if (send) {
                    toastSuccess("Job has been sent successfully!");
                }
                else {
                    toastSuccess("Job has been submited successfully!");
                }

                // window.closeModal("modal-job-address-incomplete");
                // window.closeModal("drawer-job-form");

                drawerRef.current?.close()


                if (keepOpen) {
                    setTimeout(() => {
                        createJob();
                    }, 500);
                }
            }
            else {
                toastError(res.message);
            }

            sendingSet(false);
            submitingSet(false);

        })

    }

    const [apptTimeRange, apptTimeRangeSet] = useState<boolean>(false)
    const [reminders, remindersSet] = useState<JobApptReminderItem[]>([])
    const nextId = useRef<number>(1)


    // Initialize the KTMenu dropdowns (e.g. the job status selector) once the form is actually
    // rendered. When opened from the chat "Add as Job" flow the form stays hidden behind
    // `duplicateLoading` while the message is parsed, so binding only on `opened` would run before
    // the dropdown exists in the DOM and leave it unresponsive. Re-run once the form is visible.
    useEffect(() => {
        if (opened && !duplicateLoading) {
            setTimeout(() => {
                window.dataTableMenu()
            }, 500);
        }
    }, [opened, duplicateLoading])

    // The Technician pickers' options: in the drawer's order, each with the drive on it. The same
    // array while nothing shown changes -- the classic DropDown refills itself on a new one.
    const rankedSignature = ranking.rank(techs)
        .map(r => `${r.item.id}:${r.info.distanceLabel ?? ""}:${r.info.distanceNote ?? ""}:${r.info.hasApp}:${r.info.onClock}`).join("|")
    const rankedTechs = useMemo(() => ranking.rank(techs).map(({ item, info }) => ({
        ...item,
        hasApp: info.hasApp,
        onClock: info.onClock,
        distanceLabel: info.distanceLabel ? info.distanceLabel + (info.distanceNote ? " · " + info.distanceNote : "") : undefined
        // eslint-disable-next-line react-hooks/exhaustive-deps
    })), [rankedSignature])
    const techsLoading = !!techs && !ranking.ready

    return (
        <>
            {/* On a laptop the form takes nearly the whole screen, so the map beside it gets room
                too; a sliver stays to click out by. Larger screens keep the 90%. */}
            <Drawer id="drawer-job-form" width={screen.width < 1600 ? "97%" : "90%"}
                noPadding={!screen.isMobile} noScroll={!screen.isMobile}
                noHeader={!screen.isMobile}
                title="Create Job" ref={drawerRef}
                onOpen={() => {
                    setTimeout(() => {
                        buildJobMapSet(true)
                        window.KTMenuCreate()
                    }, 100);
                }}
                onClose={() => closeJob()}>

                {
                    (duplicateLoading || !opened) ?
                        <div className="py-20">
                            <Indicator text="Please wait..." />
                        </div>
                        :
                        <div>
                            {
                                (screen.isMobile || user?.isTech) ?
                                    <div>

                                        <TextBox label="Full Name *" name="fullname" labelUp block marginBottom={5} value={fullName}
                                            placeholder="Write the customer name" onTextChanges={e => fullNameSet(e)} />

                                        {
                                            pref &&
                                            <PhoneInput id="job-form-phonenumber" label="Phone Number *" name="phoneNumber" ext labelUp block
                                                autoComplete={(pref.jobsPreferences.autofillCustomerWhileAddJob && !user?.isTech)}
                                                pastJobs={(pref.jobsPreferences.autofillCustomerWhileAddJob && !user?.isTech)}
                                                country={phoneCountry} marginBottom={5}
                                                defaultPhone={phone} defaultCountry={getCountryCache()}
                                                onCountrySelect={c => phoneCountrySet(c)}
                                                onChange={e => phoneSet(e)}
                                                onSelect={e => contactSet(e)} />
                                        }

                                        <AddressField id="jobform-address" label="Address *" name="jobAddress" block labelUp
                                            defaultCountry={getCountryCache()} country={mapCountry}
                                            //addressId={duplicateModel?.addressId}
                                            selectedAddress={address} verify showLocalTime
                                            placeholder="Search by Zip Code, Building Number, Street..."
                                            reset={formReset} parent="drawer-job-form"
                                            onCountrySelect={c => mapCountrySet(c)}
                                            onSelect={e => addressSet(e)}
                                            onUnselect={() => addressSet(undefined)}
                                            onTextChanged={e => customAddressSet(e)}
                                            marginBottom={5} />

                                        {
                                            !user?.isTech &&
                                            <DropDown id="drp-jobsources" name="jobSourceId" label="Job Source"
                                                labelUp marginBottom={5} deselect={false} source={jobSources}
                                                reset={formReset} defaultValue={jobSourceId} classicSelect
                                                onSelect={val => jobSourceIdSet(val)}
                                                onClear={() => {
                                                    jobSourceIdSet(undefined)
                                                }} />
                                        }



                                        <DropDown id="drp-jobtypes" name="jobTypeId" label="Job Type"
                                            labelUp marginBottom={5} tabIndex={8} classicSelect
                                            source={jobTypes} dataGroup="groupName"
                                            defaultValue={jobTypeId} reset={formReset} deselect={false}
                                            onSelect={val => jobTypeIdSet(val)}
                                            onClear={() => {
                                                jobTypeIdSet(undefined)
                                            }} />

                                        {
                                            !user?.isTech &&
                                            <DropDown id="drp-metros" key="drp-metros" label="Metro" name="metroId"
                                                labelUp marginBottom={5} source={jobMetros} classicSelect
                                                defaultValue={selectedMetro?.id} reset={formReset} deselect tabIndex={9}
                                                onSelectItem={e => selectedMetroSet(e)}
                                                onClear={() => {
                                                    selectedMetroSet(undefined)
                                                }} />
                                        }

                                        {
                                            !user?.isTech &&
                                            <div className="position-relative">
                                                <TechAvailabilityIndicator techId={selectedTech?.id}
                                                    className="position-absolute end-0 top-0" />
                                                <DropDown id="drp-technicians" key="drp-technicians" label="Technician"
                                                    labelUp marginBottom={5} classicSelect
                                                    disabled={techsLoading} placeholder={techsLoading ? "Working out driving times…" : undefined}
                                                    source={rankedTechs} dataText="fullName" dataValue="id" name="techId" tabIndex={10}
                                                    defaultValue={selectedTech?.id} reset={formReset} deselect
                                                    onSelectItem={e => selectedTechSet(e)}
                                                    onClear={() => selectedTechSet(undefined)} />
                                            </div>
                                        }


                                        <TextArea id="txt-jobdetails" name="jobdetails" label="Job Details"
                                            labelUp block rows={screen.height < 750 ? 3 : 6} marginBottom={5} value={jobDetails}
                                            placeholder="Describe the job..." tabIndex={11}
                                            onChange={e => jobDetailsSet(e.target.value)} />

                                        <DatePicker id="dt-jobappoitment" name="appointmentDate" drops="up"
                                            placeholder="Choose a date" icon="calendar-clock" marginBottom={10}
                                            label="Appointment Date" labelUp block empty timePicker
                                            selectedDate={appointmentDate}
                                            onPick={e => {
                                                appointmentDateSet(e.date.toDate())
                                                appointmentDateTextSet(e.value)
                                            }}
                                            onClear={() => {
                                                appointmentDateSet(undefined)
                                                appointmentDateTextSet(undefined)
                                            }} />


                                        <button type="button" className="btn btn-primary w-100 mb-3"
                                            disabled={(submiting || sending)} onClick={e => submit()}>
                                            {
                                                submiting ?
                                                    <>
                                                        <Icon icon="spinner-third" spin />
                                                        <span className="ms-2">
                                                            Please Wait
                                                        </span>
                                                    </>
                                                    : <>Submit</>
                                            }
                                        </button>

                                        {/* The same button the desktop form has had for years:
                                            the job is created and handed to the technician in one
                                            go. A technician has nobody to send their own job to. */}
                                        {
                                            !user?.isTech &&
                                            <button type="button" className="btn btn-success w-100 mb-5"
                                                disabled={(submiting || sending)} onClick={e => submitAndSendSet(true)}>
                                                {
                                                    sending ?
                                                        <>
                                                            <Icon icon="spinner-third" spin />
                                                            <span className="ms-2">
                                                                Please Wait
                                                            </span>
                                                        </>
                                                        : <>Submit &amp; Send</>
                                                }
                                            </button>
                                        }

                                        <button type="button" className="btn btn-secondary w-100"
                                            onClick={e => drawerRef.current?.close()}>
                                            Discard
                                        </button>



                                    </div>
                                    :
                                    <div className="row">


                                        <div className="col-lg-8 col-xxl-7 px-8 pb-0" style={{
                                            height: screen.height,
                                            overflowX: "hidden"
                                        }}>

                                            <FlexRow className="py-4 py-xxl-6 border-bottom-0">
                                                <div>
                                                    <h2 className="fs-1 fs-xxxl-1 modal-title d-flex align-items-center">
                                                        Create Job
                                                    </h2>
                                                </div>
                                                <div className="ms-auto">
                                                    <IconButton icon="times"
                                                        onClick={e => {
                                                            if (bodyChanged) {
                                                                window.openModal("modal-discard-job");
                                                            }
                                                            else {
                                                                keepOpenSet(false);
                                                                drawerRef.current?.close()
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </FlexRow>

                                            <div>

                                                <div className="row">
                                                    <div className="col-md-6 col-xxl-6">
                                                        {
                                                            pref &&
                                                            <PhoneInput id="job-form-phonenumber" label="Phone Number *" name="phoneNumber" parent="drawer-job-form" ext
                                                                labelUp block autoComplete={pref.jobsPreferences.autofillCustomerWhileAddJob} noMarginBottom country={phoneCountry}
                                                                autoFocus defaultPhone={phone} defaultCountry={getCountryCache()} pastJobs
                                                                onCountrySelect={c => phoneCountrySet(c)} tabIndex={1}
                                                                onChange={e => phoneSet(e)}
                                                                onSelect={e => contactSet(e)} />
                                                        }
                                                    </div>
                                                    <div className="col-md-6 col-xxl-6">
                                                        <TextBox label="Full Name *" name="fullname" labelUp block marginBottom={0} value={fullName}
                                                            placeholder="Write the customer name" tabIndex={2} onTextChanges={e => fullNameSet(e)} />
                                                    </div>
                                                </div>

                                                <div className={`row ${screen.height < 750 ? "mt-2" : "mt-4"}`}>
                                                    <div className="col-md-12">
                                                        <AddressField id="jobform-address" label="Address *" name="jobAddress" block labelUp
                                                            defaultCountry={getCountryCache()} country={mapCountry} tabIndex={6}
                                                            //addressId={duplicateModel?.addressId}
                                                            selectedAddress={address} verify showLocalTime
                                                            placeholder="Search by Zip Code, Building Number, Street..."
                                                            reset={formReset} parent="drawer-job-form"
                                                            onCountrySelect={c => mapCountrySet(c)}
                                                            onSelect={e => addressSet(e)}
                                                            onUnselect={() => addressSet(undefined)}
                                                            onTextChanged={e => customAddressSet(e)}
                                                            noMarginBottom />
                                                    </div>
                                                </div>

                                                <div id="job-form-client-more-details" className={`${screen.height < 750 ? "mt-2" : "mt-4"}`} style={{ display: "none" }}>
                                                    <div className="row">
                                                        <div className="col-md-4">
                                                            <PhoneInput key={"secondary-phone-" + contactFillKey} label="Secondary Phone" labelUp block country={country} ext pastJobs
                                                                noMarginBottom reset={formReset} tabIndex={3} defaultPhone={contactPhone}
                                                                onChange={e => contactPhoneSet(e)} />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <TextBox label="Company" labelUp block marginBottom={0} tabIndex={4}
                                                                value={company} placeholder="Write the company name"
                                                                onTextChanges={e => companySet(e)} />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <TextBox label="Email Address" labelUp block marginBottom={0} tabIndex={5}
                                                                value={email} placeholder="Write the customer email address"
                                                                onTextChanges={e => emailSet(e)} />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div id="job-form-client-more-details-btn" className={`row ${screen.height < 750 ? "" : "mt-2"}`}>
                                                    <div className="col-12">
                                                        <a href="#" className="fs-8" tabIndex={-1}
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                if (showMore) {
                                                                    showMoreSet(false);
                                                                    $("#job-form-client-more-details").slideUp(200);
                                                                }
                                                                else {
                                                                    showMoreSet(true);
                                                                    $("#job-form-client-more-details").slideDown(200);
                                                                }

                                                            }}>
                                                            {
                                                                showMore ? <><Icon icon="chevron-up" className="me-1" size={11} /> Show Less</>
                                                                    : <><Icon icon="chevron-down" className="me-1" size={11} /> Show More</>
                                                            }
                                                        </a>
                                                    </div>
                                                </div>


                                                <div className={`row ${screen.height < 750 ? "mt-2" : "mt-4"}`}>
                                                    <div className="col-12">
                                                        <div className="row">
                                                            <div className="col-md-3">
                                                                <Select id="drp-jobsources" name="jobSourceId" label="Job Source" labelUp marginBottom={0} deselect={false}
                                                                    source={[...jobSources].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))}
                                                                    reset={formReset} value={jobSourceId} tabIndex={7}
                                                                    onSelect={val => jobSourceIdSet(val)}
                                                                    onClear={() => {
                                                                        jobSourceIdSet(undefined)
                                                                    }} />
                                                            </div>

                                                            <div className="col-md-3">
                                                                <Select id="drp-jobtypes" name="jobTypeId" label="Job Type" labelUp marginBottom={0} tabIndex={8}
                                                                    source={jobTypes} dataGroup="groupName"
                                                                    value={jobTypeId} reset={formReset} deselect={false}
                                                                    onSelect={val => jobTypeIdSet(val)}
                                                                    onClear={() => {
                                                                        jobTypeIdSet(undefined)
                                                                    }} />
                                                            </div>

                                                            <div className="col-md-3">
                                                                <Select id="drp-metros" label="Metro" name="metroId" labelUp marginBottom={0}
                                                                    source={metroOptions}
                                                                    optionTemplate={renderMetroOption} selectionTemplate={renderMetroSelection}
                                                                    value={selectedMetro?.id} reset={formReset} deselect tabIndex={9}
                                                                    onSelectItem={e => selectedMetroSet(e)}
                                                                    onClear={() => {
                                                                        selectedMetroSet(undefined)
                                                                    }} />
                                                            </div>

                                                            <div className="col-md-3 position-relative">
                                                                <TechAvailabilityIndicator techId={selectedTech?.id}
                                                                    className="position-absolute end-0 top-0" />
                                                                <Select id="drp-technicians" label="Technician" labelUp marginBottom={0}
                                                                    disabled={techsLoading} placeholder={techsLoading ? "Working out driving times…" : undefined}
                                                                    source={rankedTechs} dataText="fullName" dataValue="id" name="techId" tabIndex={10}
                                                                    value={selectedTech?.id} reset={formReset} deselect
                                                                    optionTemplate={renderTechOption} selectionTemplate={renderTechSelection}
                                                                    onSelectItem={e => selectedTechSet(e)}
                                                                    onClear={() => selectedTechSet(undefined)} />
                                                            </div>

                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={`row ${screen.height < 750 ? "mt-2" : "mt-4"}`}>
                                                    <div className="col-12">
                                                        <TextArea id="txt-jobdetails" name="jobdetails" label="Job Details"
                                                            labelUp block rows={screen.height < 750 ? 3 : 6} marginBottom={0} value={jobDetails}
                                                            placeholder="Describe about the job..." tabIndex={11}
                                                            onChange={e => jobDetailsSet(e.target.value)} />
                                                    </div>
                                                </div>

                                                <div className={`row ${screen.height < 750 ? "mt-2" : "mt-4"}`}>
                                                    <div className="col-12">

                                                        <Tagify id="tagify-job-flags"
                                                            source={flagsBank}
                                                            //apiUrl={API_ROUTES.TagifyFlags}
                                                            placeholder="Select Flag(s)" icon="flag"
                                                            labelUp noMarginBottom defaultIds={flags} clear={formReset}
                                                            onChange={ids => flagsSet(ids)}
                                                        />

                                                    </div>
                                                </div>

                                                <div className={`row ${screen.height < 750 ? "mt-2" : "mt-4"}`}>
                                                    <div className="col-12">

                                                        <Tagify id="tagify-job-tags"
                                                            source={tagsBank}
                                                            placeholder="Select or Enter Tag(s)" icon="tag"
                                                            freeTags labelUp noMarginBottom defaultIds={tags} clear={formReset}
                                                            onChange={ids => tagsSet(ids)}
                                                        />

                                                    </div>
                                                </div>

                                                <div className={`row ${screen.height < 750 ? "mt-2" : "mt-4"}`}>
                                                    <div className="col-md-12">
                                                        <FlexRow>

                                                            <DatePicker id="dt-jobappoitment" name="appointmentDate" drops="up"
                                                                placeholder="Appointment Date" icon="calendar-clock" marginBottom={0}
                                                                labelUp block empty selectedDate={appointmentDate}
                                                                onPick={e => {
                                                                    appointmentDateSet(e.date.toDate())
                                                                    appointmentDateTextSet(e.value)
                                                                }}
                                                                onClear={() => {
                                                                    appointmentDateSet(undefined)
                                                                    appointmentDateTextSet(undefined)
                                                                }} />

                                                            <div className="w-150px ms-2">
                                                                <TimePicker id="job-form-time-appointmentFrom" name="appointmentFrom" noMarginBottom
                                                                    labelUp value={appointmentTimeFrom} parentId="drawer-job-form"
                                                                    placeholder={`Arrival${((apptTimeRange || pref?.jobsPreferences.forceTimeRangeForAppt) ? " From" : "")}`}
                                                                    onTimePicked={t => {
                                                                        appointmentTimeFromSet(t.label)
                                                                        minToTimeSet(t.time)
                                                                    }}
                                                                    onClear={() => {
                                                                        appointmentTimeFromSet(undefined)
                                                                        minToTimeSet(0)
                                                                    }} />
                                                            </div>

                                                            {
                                                                pref?.jobsPreferences.forceTimeRangeForAppt ?
                                                                    <div className="w-150px ms-2">
                                                                        <TimePicker id="job-form-time-appointmentTo" name="appointmentTo" min={minToTime} noMarginBottom nextDay
                                                                            labelUp value={appointmentTimeTo} placeholder="Arrival To"
                                                                            time={minToTime ? (minToTime + ((pref.jobsPreferences.defaultTimeRangeForAppt ?? 1) * 60)) : undefined}
                                                                            onTimePicked={t => appointmentTimeToSet(t.label)} parentId="drawer-job-form"
                                                                            onClear={() => appointmentTimeToSet(undefined)}
                                                                        />
                                                                    </div>
                                                                    :
                                                                    <>
                                                                        {
                                                                            apptTimeRange ?
                                                                                <div className="ms-2">
                                                                                    <button type="button" className="btn btn-primary btn-icon btn-sm"
                                                                                        onClick={e => apptTimeRangeSet(false)}>
                                                                                        <Icon icon="chevron-right" size={16} />
                                                                                    </button>
                                                                                </div>
                                                                                :
                                                                                <div className="ms-2">
                                                                                    <button type="button" className="btn btn-light btn-icon btn-sm"
                                                                                        onClick={e => apptTimeRangeSet(true)}>
                                                                                        <Icon icon="chevron-right" size={16} />
                                                                                    </button>
                                                                                </div>
                                                                        }

                                                                        {
                                                                            apptTimeRange &&
                                                                            <div className="w-150px ms-2">
                                                                                <TimePicker id="job-form-time-appointmentTo" name="appointmentTo" min={minToTime} noMarginBottom nextDay
                                                                                    labelUp defaultValue={appointmentTimeTo} placeholder="Arrival To" time={minToTime ? (minToTime + 60) : undefined}
                                                                                    onTimePicked={t => appointmentTimeToSet(t.label)} parentId="drawer-job-form"
                                                                                    onClear={() => appointmentTimeToSet(undefined)}
                                                                                />
                                                                            </div>
                                                                        }
                                                                    </>
                                                            }





                                                        </FlexRow>

                                                        {
                                                            appointmentDate && appointmentTimeFrom &&
                                                            <div className="mt-5">

                                                                {
                                                                    reminders.map(r =>
                                                                        <FlexRow key={`reminder-item-${r.id}`} className="mb-4">
                                                                            <button className="btn btn-sm btn-light-danger btn-icon me-2"
                                                                                onClick={e => {
                                                                                    e.preventDefault();
                                                                                    remindersSet(prev => prev.filter(f => f.id != r.id))
                                                                                }}>
                                                                                <Icon icon="minus" />
                                                                            </button>
                                                                            <div className="me-2" style={{ width: 120 }}>
                                                                                <select key={`reminder-type-${r.id}`} className="form-select form-select-sm"
                                                                                    value={r.type} onChange={e => {
                                                                                        var t = Number.parseInt(e.target.value);
                                                                                        remindersSet(p =>
                                                                                            p.map(f => f.id == r.id ? { ...f, type: t } : f)
                                                                                        )
                                                                                    }}>
                                                                                    <option value={1}>Minutes</option>
                                                                                    <option value={2}>Hours</option>
                                                                                    <option value={3}>Custom</option>
                                                                                </select>
                                                                            </div>
                                                                            {
                                                                                r.type == 3 ?
                                                                                    <div className="me-2" style={{ width: 200 }}>
                                                                                        <DatePicker key={`reminder-datepicker-${r.id}`} id={`reminder-datepicker-${r.id}`} name={`reminder-date-${r.id}`}
                                                                                            preventClear noTextError timePicker block marginBottom={0} small icon="calendar-clock" selectedDate={r.date} drops="up"
                                                                                            onPick={e => {
                                                                                                remindersSet(p =>
                                                                                                    p.map(f => f.id == r.id ? {
                                                                                                        ...f,
                                                                                                        date: e.value
                                                                                                    } : f)
                                                                                                )
                                                                                            }} />
                                                                                    </div>
                                                                                    :
                                                                                    <div className="me-2" style={{ width: 200 }}>
                                                                                        <TextBox key={`reminder-amount-${r.id}`} name={`reminder-amount-${r.id}`}
                                                                                            block noMarginBottom value={r.amount} numeric maxLength={3} labelUp noTextError
                                                                                            small iconText={r.type == 1 ? "Minutes Before" : "Hours Before"}
                                                                                            onTextChanges={txt => {
                                                                                                remindersSet(p =>
                                                                                                    p.map(f => f.id == r.id ? { ...f, amount: isNaN(Number.parseInt(txt)) ? 0 : Number.parseInt(txt) } : f)
                                                                                                )
                                                                                            }} />
                                                                                    </div>
                                                                            }

                                                                            <div className="flex-grow-1">
                                                                                <select key={`reminder-action-${r.id}`} className="form-select form-select-sm"
                                                                                    value={r.action} onChange={e => {
                                                                                        var t = Number.parseInt(e.target.value);
                                                                                        remindersSet(p =>
                                                                                            p.map(f => f.id == r.id ? { ...f, action: t } : f)
                                                                                        )
                                                                                    }}>
                                                                                    <option value={1}>Move to Top & Send to Technician</option>
                                                                                    <option value={2}>Move to Top</option>
                                                                                    <option value={3}>Send to Technician</option>
                                                                                </select>
                                                                            </div>

                                                                        </FlexRow>
                                                                    )
                                                                }

                                                                <button className="btn btn-sm btn-outline btn-outline-dashed btn-outline-secondary btn-active-light-secondary text-muted"
                                                                    onClick={e => {
                                                                        e.preventDefault();
                                                                        remindersSet(p => [...p, {
                                                                            id: nextId.current++,
                                                                            type: 1,
                                                                            amount: 60,
                                                                            action: 1
                                                                        }])
                                                                    }}>
                                                                    <Icon icon="plus" />
                                                                    Add Reminder
                                                                </button>

                                                            </div>
                                                        }

                                                    </div>
                                                    <div className="col-md-3 col-xxl-2">

                                                    </div>
                                                    <div className="col-md-3 col-xxl-2">

                                                    </div>
                                                </div>

                                                <div className="row border-top pt-xxl-6 mt-xxl-6 pt-2 mb-10 mt-2">
                                                    <div className="col-md-6">

                                                        <FlexRow>
                                                            <div>
                                                                <button type="button" className="btn btn-bg-primary btn-sm"
                                                                    style={{
                                                                        backgroundColor: selectedStatus?.color,
                                                                        color: "#fff"
                                                                    }}
                                                                    data-kt-menu-trigger="click"
                                                                    data-kt-menu-placement="bottom-start">
                                                                    {selectedStatus?.name}
                                                                    <span className="ms-3">
                                                                        <Icon icon="chevron-down" color="white" />
                                                                    </span>
                                                                </button>
                                                                <div className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-200px py-4" data-kt-menu="true">
                                                                    {
                                                                        statuses?.map((item, key) =>
                                                                            <div key={key} className="menu-item">
                                                                                <a href="#" className="menu-link px-3" onClick={e => {
                                                                                    e.preventDefault()
                                                                                    selectedStatusSet(item)
                                                                                }}>
                                                                                    <Icon icon="circle" type="solid" className="me-2" color={item.color} />
                                                                                    {item.name}
                                                                                </a>
                                                                            </div>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                            <div className="form-check form-check-custom form-check-solid form-check-sm ms-3">
                                                                <input className="form-check-input" type="checkbox"
                                                                    checked={keepOpen} id="chk-job-form-keep-it-open"
                                                                    onChange={e => keepOpenSet(e.target.checked)} />
                                                                <label className="form-check-label ms-1" htmlFor="chk-job-form-keep-it-open">
                                                                    Create Another Job
                                                                </label>
                                                            </div>
                                                        </FlexRow>


                                                    </div>
                                                    <div className="col-md-6 text-end">
                                                        <button type="button" className="btn btn-secondary btn-sm"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                if (bodyChanged) {
                                                                    window.openModal("modal-discard-job");
                                                                }
                                                                else {
                                                                    keepOpenSet(false);
                                                                    drawerRef.current?.close()
                                                                }
                                                            }}>
                                                            Discard
                                                        </button>
                                                        <button type="button" className="btn btn-success btn-sm ms-2 w-125px" disabled={(submiting || sending)} onClick={e => submitAndSendSet(true)}>
                                                            {
                                                                sending ?
                                                                    <>
                                                                        <Icon icon="spinner-third" spin />
                                                                        <span className="ms-2">
                                                                            Please Wait
                                                                        </span>
                                                                    </>
                                                                    : <>Submit & Send</>
                                                            }
                                                        </button>
                                                        <button type="button" className="btn btn-primary btn-sm ms-2 w-125px" disabled={(submiting || sending)} onClick={e => submit()}>
                                                            {
                                                                submiting ?
                                                                    <>
                                                                        <Icon icon="spinner-third" spin />
                                                                        <span className="ms-2">
                                                                            Please Wait
                                                                        </span>
                                                                    </>
                                                                    : <>Submit</>
                                                            }
                                                        </button>
                                                    </div>
                                                </div>

                                            </div>



                                        </div>

                                        <div className="col-lg-4 col-xxl-5 position-relative">

                                            <JobFormMap height={screen.height} country={mapCountry}
                                                address={address}
                                                techs={techs} selectedTech={selectedTech}
                                                routeLabel={selectedTech ? ranking.infoFor(selectedTech).routeLabel : undefined}
                                                metros={jobMetros} selectedMetro={selectedMetro}
                                                onZoomChanged={e => mapZoomSet(e)}
                                                onAddressMoved={e => addressSet(e)}
                                                onSelectMetro={mtr => selectedMetroSet(mtr)}
                                                onSelectTech={tech => selectedTechSet(techs?.find(t => t.id === tech.id) ?? tech)} />

                                            {
                                                selectedMetro?.description && selectedMetro.description.length > 0 &&
                                                <div className="bg-light" style={{
                                                    position: "absolute",
                                                    zIndex: 2000,
                                                    bottom: 20,
                                                    left: 30,
                                                    width: "60%",
                                                    minHeight: 100,
                                                    padding: 10,
                                                    opacity: .8
                                                }}>
                                                    <FlexRow className="mb-2">
                                                        <Icon icon="circle" type="solid" color={selectedMetro?.color} className="me-2" />
                                                        <span className="fw-bolder">{selectedMetro.name}</span>
                                                    </FlexRow>
                                                    <div className="fs-7 line-break">
                                                        {selectedMetro.description}
                                                    </div>
                                                </div>
                                            }



                                        </div>


                                    </div>
                            }

                        </div>
                }


            </Drawer>


            <Modal id="modal-job-address-incomplete"
                title="Incomplete Address"
                titleIcon="triangle-exclamation"
                buttonText="Submit Anyway"
                cancelText="Fix the Address"
                buttonColor="warning" secondModal
                onConfirm={callback => {
                    if (window.isNullOrEmpty(selectedState)) {
                        window.formError("incompleted-states", "Please select a valid state!", "modal-job-address-incomplete");
                        callback(true);
                        return;
                    }
                    submit(submitAndSend, true)
                    callback()
                }}>
                <div className="mb-5">
                    The job address appears to be incomplete. You can still submit the job, but please double-check to ensure accuracy and completeness.
                </div>

                <TextBox id="txt-incompleted-address" value={customAddress} label="Address" labelUp />

                <div className="row">
                    <div className="col-md-6">
                        <TextBox id="txt-incompleted-zipcode" value={zipcode}
                            label="Zip Code *" labelUp onTextChanges={zip => {
                                if (zip.length > 4) {
                                    hub.LookUpZipcode(zip, country.alpha3Code ?? "usa", res => {
                                        zipcodeSet(zip);
                                        selectedStateSet(res?.address?.stateId);
                                    })
                                }
                                else {
                                    zipcodeSet(zip);
                                    selectedStateSet(undefined);
                                }

                            }} />
                    </div>
                    <div className="col-md-6">
                        <DropDown id="drp-incompleted-states"
                            forceSelectValue={selectedState}
                            name="incompleted-states"
                            source={states} parentModal="modal-job-address-incomplete"
                            label="State" labelUp
                            onSelect={e => selectedStateSet(e)}
                            onClear={() => selectedStateSet(undefined)} />
                    </div>
                </div>

            </Modal>

            <Modal id="modal-noapptreminder-confirm"
                title="Alert"
                secondModal
                onConfirm={callback => {
                    submit(submitAndSend, true)
                    callback()
                }}
                buttonText="Confirm & Submit the Job"
                buttonColor="warning">

                <div className="my-10">
                    You haven't added any reminders for this appointment. If you continue, the reminder will be sent based on the technician's default settings. Are you sure you want to proceed?
                </div>

            </Modal>

            <Modal id="modal-discard-job"
                title="Discard Job"
                buttonText="Discard Job"
                cancelText="Cancel"
                buttonColor="warning" secondModal
                onConfirm={callback => {
                    keepOpenSet(false);
                    callback(true);
                    window.closeModal("modal-discard-job");
                    drawerRef.current?.close()
                }}>
                <div>
                    Are you sure you want to discard the changes and close the form?
                </div>
            </Modal>


        </>
    )
}